"use client";

import { useEffect, useRef, useState } from "react";
import { PixelText } from "@/components/ui/pixel-text";

type Props = {
  current: number;
  max: number;
  label?: string;
  showNumeric?: boolean;
  width?: number;
  height?: number;
  tweenMs?: number;
};

const COLOR_HIGH = "#4a9c3e";
const COLOR_MID = "#c5a83a";
const COLOR_LOW = "#b04848";
const COLOR_BORDER = "#1a1a1a";
const COLOR_HIGHLIGHT = "rgba(255, 255, 255, 0.25)";
const COLOR_BG = "#f5ecd6";

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

function colorForPercent(pct: number): string {
  if (pct > 0.5) return COLOR_HIGH;
  if (pct > 0.2) return COLOR_MID;
  return COLOR_LOW;
}

export function BattleHPBar({
  current,
  max,
  label,
  showNumeric = true,
  width = 200,
  height = 8,
  tweenMs = 500,
}: Props) {
  const safeMax = Math.max(1, max);
  const clampedCurrent = Math.max(0, Math.min(current, safeMax));

  const [displayed, setDisplayed] = useState<number>(clampedCurrent);
  const previousRef = useRef<number>(clampedCurrent);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const from = previousRef.current;
    const to = clampedCurrent;

    if (from === to) {
      setDisplayed(to);
      return;
    }

    const start = performance.now();
    const duration = Math.max(1, tweenMs);

    const step = (now: number) => {
      const elapsed = now - start;
      const t = Math.min(1, elapsed / duration);
      const eased = easeOutCubic(t);
      const value = from + (to - from) * eased;
      setDisplayed(value);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        previousRef.current = to;
        rafRef.current = null;
      }
    };

    rafRef.current = requestAnimationFrame(step);

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      previousRef.current = to;
    };
  }, [clampedCurrent, tweenMs]);

  const percent = displayed / safeMax;
  const fillWidth = Math.max(0, Math.min(1, percent)) * width;
  const fillColor = clampedCurrent === 0 ? COLOR_LOW : colorForPercent(percent);

  return (
    <div
      className="flex items-center gap-2"
      style={{ imageRendering: "pixelated" }}
    >
      {label ? (
        <PixelText size="sm" glow="none" className="tracking-wider">
          {label}
        </PixelText>
      ) : null}

      <div
        className="relative"
        style={{
          width,
          height,
          backgroundColor: COLOR_BG,
          border: `2px solid ${COLOR_BORDER}`,
          boxSizing: "content-box",
          imageRendering: "pixelated",
        }}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={safeMax}
        aria-valuenow={clampedCurrent}
        aria-label={label ?? "progress"}
      >
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            width: fillWidth,
            height,
            backgroundColor: fillColor,
            transition: "background-color 120ms linear",
          }}
        />
        <div
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            width: "100%",
            height: 1,
            backgroundColor: COLOR_HIGHLIGHT,
            pointerEvents: "none",
          }}
        />
      </div>

      {showNumeric ? (
        <PixelText size="sm" glow="none" className="tracking-wider tabular-nums">
          {Math.round(clampedCurrent)} / {safeMax}
        </PixelText>
      ) : null}
    </div>
  );
}

export function BattleEXPBar(props: Omit<Props, "label" | "showNumeric"> & {
  label?: string;
  showNumeric?: boolean;
}) {
  const { label = "EXP", showNumeric = false, ...rest } = props;
  return <BattleHPBar {...rest} label={label} showNumeric={showNumeric} />;
}

export default BattleHPBar;
